import type React from 'react';
import { useEffect, useState } from 'react';

import { EYE_STATUS_OPTIONS, MOUTH_STATUS_OPTIONS } from '@vlabs/api-bindings/src/constants';
import type { WithClassNameProp, WithDataTestid } from '@vlabs/uikit/types';
import cn from 'classnames';

import { useEventApi } from '@vlabs/monokit/entities/events/api';
import { buildTestid } from '@vlabs/monokit/shared/lib/utils';

import st from './AdditionalPropertiesBySampleId.module.sass';
import { useAdditionalPropertiesBySampleIdTranslation } from './i18n';
import { GeneralEventInfoRow } from '../../general-event/@x/event-details';

interface AdditionalPropertiesBySampleIdProps extends WithClassNameProp, WithDataTestid {
  sampleId: string;
}

const displayName = 'AdditionalPropertiesBySampleId';
const testId = buildTestid(displayName);

const SDK_PARAMS = {
  image_type: 0,
  estimate_quality: 1,
  estimate_head_pose: 1,
  estimate_gaze: 1,
  estimate_eyes_attributes: 1,
  estimate_mouth_attributes: 1,
};

function formatValue(value: unknown): React.ReactNode {
  if (typeof value === 'number') return value.toFixed(5);
  if (value === null || value === undefined || value === '') return '-';

  return value;
}

function formatMouthState(mouthAttributes: Record<string, unknown> | undefined): React.ReactNode {
  const { score, ...attributes } = mouthAttributes ?? {};

  // берем состояние рта с самой высокой вероятностью
  const [state, stateScore] = Object
    .entries(attributes)
    .filter((entry): entry is [string, number] => typeof entry[1] === 'number')
    .sort((first, second) => second[1] - first[1])[0]
    ?? [];

  if (!state || stateScore === undefined) return '-';

  return `${(MOUTH_STATUS_OPTIONS as Record<string, { label?: string } | undefined>)[state]?.label ?? state} (${stateScore.toFixed(5)})`;
}

function formatEyesState(
  eyesAttributes: Record<string, unknown> | undefined,
  leftEyeLabel: string,
  rightEyeLabel: string,
): React.ReactNode {
  const leftEye = eyesAttributes?.left_eye as Record<string, unknown> | undefined;
  const rightEye = eyesAttributes?.right_eye as Record<string, unknown> | undefined;
  const leftEyeState = leftEye?.state as string | undefined;
  const rightEyeState = rightEye?.state as string | undefined;

  if (!leftEyeState && !rightEyeState) return '-';

  // у каждого глаза свое состояние, поэтому выводим их отдельными строками
  return (
    <div className={st.Stack}>
      <div>
        {`${leftEyeLabel} - ${leftEyeState ? (EYE_STATUS_OPTIONS as Record<string, { label?: string } | undefined>)[leftEyeState]?.label ?? leftEyeState : '-'}`}
      </div>
      <div>
        {`${rightEyeLabel} - ${rightEyeState ? (EYE_STATUS_OPTIONS as Record<string, { label?: string } | undefined>)[rightEyeState]?.label ?? rightEyeState : '-'}`}
      </div>
    </div>
  );
}

function AdditionalPropertiesBySampleId({
  sampleId,
  className = undefined,
  'data-testid': dataTestId = undefined,
}: AdditionalPropertiesBySampleIdProps): React.JSX.Element {
  const { t } = useAdditionalPropertiesBySampleIdTranslation();
  const api = useEventApi();
  const [extractedAttributes, setExtractedAttributes] = useState<Record<string, unknown>>();

  const faceEstimation = ((extractedAttributes?.images_estimations as unknown[] | undefined)?.[0] as
    | Record<string, unknown>
    | undefined)?.estimations?.[0]?.face?.detection as
    | Record<string, unknown>
    | undefined;
  const attributes = faceEstimation?.attributes as Record<string, unknown> | undefined;
  const quality = faceEstimation?.quality as Record<string, unknown> | undefined;

  // склеиваем данные в список строк, чтобы не дублировать верстку
  const rows = [
    [t('light'), quality?.light],
    [t('dark'), quality?.dark],
    [t('blurriness'), quality?.blurriness],
    [t('specularity'), quality?.specularity],
    [t('illumination'), quality?.illumination],
    [t('head_pose_roll'), (attributes?.head_pose as Record<string, unknown> | undefined)?.roll],
    [t('head_pose_pitch'), (attributes?.head_pose as Record<string, unknown> | undefined)?.pitch],
    [t('head_pose_yaw'), (attributes?.head_pose as Record<string, unknown> | undefined)?.yaw],
    [t('gaze_pitch'), (attributes?.gaze as Record<string, unknown> | undefined)?.pitch],
    [t('gaze_yaw'), (attributes?.gaze as Record<string, unknown> | undefined)?.yaw],
    [t('mouth_state'), formatMouthState(attributes?.mouth_attributes as Record<string, unknown> | undefined)],
    [t('eyes_state'), formatEyesState(attributes?.eyes_attributes as Record<string, unknown> | undefined, t('left_eye'), t('right_eye'))],
  ] satisfies [string, unknown][];

  useEffect(() => {
    const requestAttributes = async () => {
      setExtractedAttributes(undefined);

      // head_pose и gaze считаются только по raw image, поэтому повторяем старую схему:
      // сначала получаем картинку sample, потом отправляем ее в SDK как image_type: 0
      const image = await api.getSample('faces', sampleId);
      if (!image) return;

      const response = await api.estimateSdk(image, SDK_PARAMS);
      setExtractedAttributes(response?.data);
    };

    requestAttributes();
  }, [api, sampleId]);

  return (
    <div
      className={cn(st.Root, className)}
      {...testId(dataTestId)}
    >
      {rows.map(([title, value]) => (
        <GeneralEventInfoRow
          className={st.Row}
          key={title}
          title={title}
          titleClassName={st.RowTitle}
        >
          {formatValue(value)}
        </GeneralEventInfoRow>
      ))}
    </div>
  );
}

AdditionalPropertiesBySampleId.displayName = displayName;

export { AdditionalPropertiesBySampleId };
